import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type DERC, type DERCRequest, type DERCFiltered } from '../types/derc';
import { DERCService } from '../services/dercService';
import { errorService } from '../../../../services/errorService';

interface DERCState {
  // State
  dercs: DERC[];
  filteredDERCs: DERCFiltered[];
  loading: boolean;
  error: string | null;
  selectedDERC: DERC | null;

  // UI State
  dialogOpen: boolean;

  // Actions
  fetchDERCs: () => Promise<void>;
  fetchFilteredDERCs: () => Promise<void>;
  createDERC: (dercRequest: DERCRequest) => Promise<void>;
  updateDERC: (dercRequest: DERCRequest) => Promise<void>;
  saveDERC: (dercRequest: DERCRequest) => Promise<DERC | null>;
  deleteDERC: (id: number) => Promise<void>;
  toggleDERCStatus: (id: number) => Promise<void>;
  getDERCById: (id: number) => Promise<DERC | null>;

  // UI Actions
  setSelectedDERC: (derc: DERC | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  dercs: [],
  filteredDERCs: [],
  loading: false,
  error: null,
  selectedDERC: null,
  dialogOpen: false,
};

export const useDERCStore = create<DERCState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchDERCs: async () => {
        set({ loading: true, error: null }, false, 'dercs/fetchDERCs/start');

        try {
          const dercs = await DERCService.getDERCs();
          set(
            { dercs, loading: false },
            false,
            'dercs/fetchDERCs/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch DERCs',
            },
            false,
            'dercs/fetchDERCs/error'
          );
        }
      },

      fetchFilteredDERCs: async () => {
        set({ loading: true, error: null }, false, 'dercs/fetchFilteredDERCs/start');

        try {
          const filteredDERCs = await DERCService.getFilteredDERCs();
          set(
            { filteredDERCs, loading: false },
            false,
            'dercs/fetchFilteredDERCs/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch filtered DERCs',
            },
            false,
            'dercs/fetchFilteredDERCs/error'
          );
        }
      },

      createDERC: async (dercRequest: DERCRequest) => {
        set({ loading: true, error: null }, false, 'dercs/createDERC/start');

        try {
          const newDERC = await DERCService.saveDERC(dercRequest);
          set(
            (state) => ({
              dercs: [...state.dercs, newDERC],
              loading: false,
              dialogOpen: false,
              selectedDERC: null,
            }),
            false,
            'dercs/createDERC/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to create DERC',
            },
            false,
            'dercs/createDERC/error'
          );
        }
      },

      updateDERC: async (dercRequest: DERCRequest) => {
        if (!dercRequest.id) {
          set(
            { error: 'DERC ID is required for update' },
            false,
            'dercs/updateDERC/error'
          );
          return;
        }

        set({ loading: true, error: null }, false, 'dercs/updateDERC/start');

        try {
          const updatedDERC = await DERCService.saveDERC(dercRequest);
          set(
            (state) => ({
              dercs: state.dercs.map((derc) =>
                derc.id === dercRequest.id ? updatedDERC : derc
              ),
              loading: false,
              dialogOpen: false,
              selectedDERC: null,
            }),
            false,
            'dercs/updateDERC/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to update DERC',
            },
            false,
            'dercs/updateDERC/error'
          );
        }
      },

      saveDERC: async (dercRequest: DERCRequest): Promise<DERC | null> => {
        const isUpdate = !!dercRequest.id;
        set({ loading: true, error: null }, false, 'dercs/saveDERC/start');

        try {
          const savedDERC = await DERCService.saveDERC(dercRequest);
          set(
            (state) => ({
              dercs: isUpdate
                ? state.dercs.map((derc) => derc.id === dercRequest.id ? savedDERC : derc)
                : [...state.dercs, savedDERC],
              loading: false,
              dialogOpen: false,
              selectedDERC: null,
            }),
            false,
            isUpdate ? 'dercs/saveDERC/update/success' : 'dercs/saveDERC/create/success'
          );
          errorService.showSuccess(isUpdate ? 'DERC actualizado exitosamente' : 'DERC creado exitosamente');
          return savedDERC;
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to save DERC',
            },
            false,
            'dercs/saveDERC/error'
          );
          return null;
        }
      },

      deleteDERC: async (id: number) => {
        set({ loading: true, error: null }, false, 'dercs/deleteDERC/start');

        try {
          await DERCService.deleteDERC(id);
          set(
            (state) => ({
              dercs: state.dercs.filter((derc) => derc.id !== id),
              loading: false,
            }),
            false,
            'dercs/deleteDERC/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to delete DERC',
            },
            false,
            'dercs/deleteDERC/error'
          );
        }
      },

      toggleDERCStatus: async (id: number) => {
        set({ loading: true, error: null }, false, 'dercs/toggleDERCStatus/start');

        try {
          const updatedDERC = await DERCService.toggleDERCStatus(id);
          set(
            (state) => ({
              dercs: state.dercs.map((derc) =>
                derc.id === id ? updatedDERC : derc
              ),
              loading: false,
            }),
            false,
            'dercs/toggleDERCStatus/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to toggle DERC status',
            },
            false,
            'dercs/toggleDERCStatus/error'
          );
        }
      },

      getDERCById: async (id: number): Promise<DERC | null> => {
        try {
          const dercResponse = await DERCService.getDERCById(id);
          return dercResponse;
        } catch (error) {
          set(
            {
              error: error instanceof Error ? error.message : 'Failed to get DERC by ID',
            },
            false,
            'dercs/getDERCById/error'
          );
          return null;
        }
      },

      // UI Actions
      setSelectedDERC: (derc: DERC | null) => {
        set({ selectedDERC: derc }, false, 'dercs/setSelectedDERC');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'dercs/openDialog');
      },

      closeDialog: () => {
        set(
          { dialogOpen: false, selectedDERC: null },
          false,
          'dercs/closeDialog'
        );
      },

      clearError: () => {
        set({ error: null }, false, 'dercs/clearError');
      },

      reset: () => {
        set(initialState, false, 'dercs/reset');
      },
    }),
    {
      name: 'derc-store',
    }
  )
);